import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Error as ErrorIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { saveAs } from "file-saver";
import WebsiteStatusResult from "./Results/WebsiteStatusResult";
import WebPresenceResult from "./Results/WebPresenceResult";
import {
  humanizeString,
  tzAwareHumanizeTimeString,
} from "../../utilities/formatting";
import APIEnquiry from "../../types/APIEnquiry";
import APIMonitor from "../../types/APIMonitor";
import { useTrueBizApi } from "../../api";
import APIPagedEnqueries from "../../types/APIPagedEnquiries";

const RESULTS_PER_PAGE = 15;

export default function MonitoringItemHistoryTable({
  monitor,
}: {
  monitor: APIMonitor;
}) {
  const api = useTrueBizApi();
  const [loading, setLoading] = useState<boolean>(true);
  const [enquiries, setEnquiries] = useState<APIPagedEnqueries | null>(null);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    const offset = (page - 1) * RESULTS_PER_PAGE;

    const loadEnquiries = async () => {
      try {
        const apiEnquiries = await api.getEnquiriesByMonitor(
          offset,
          RESULTS_PER_PAGE,
          monitor
        );
        setEnquiries(apiEnquiries);
      } finally {
        setLoading(false);
      }
    };

    loadEnquiries();
  }, [api, page, monitor]);

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2, mb: 2 }}>
      <Box>
        <Typography component="h2" fontWeight={700}>
          Monitor Results History
        </Typography>
      </Box>

      {loading ? (
        <CircularProgress size={28} sx={{ marginLeft: 1 }} />
      ) : (
        <>
          <TableContainer component={Box} sx={{ marginTop: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Details</TableCell>

                  <TableCell>
                    <strong>Time</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>

                  <TableCell align="center">
                    <strong>Flagged</strong>
                  </TableCell>

                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(enquiries?.items || []).map((item) => {
                  return <MonitoringHistoryRow key={item.id} enquiry={item} />;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {enquiries && enquiries.count > RESULTS_PER_PAGE && (
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <Pagination
                siblingCount={3}
                count={Math.ceil(enquiries.count / RESULTS_PER_PAGE)}
                page={page}
                onChange={(_: React.ChangeEvent<unknown>, value: number) => {
                  setPage(value);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

function MonitoringHistoryRow({ enquiry }: { enquiry: APIEnquiry }) {
  const api = useTrueBizApi();
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [downloadDisabled, setDownloadDisabled] =
    React.useState<boolean>(false);

  const URIParts = enquiry.link ? enquiry.link.split("/") : null;

  // We're going to assume that the URI is indicating an enquiry for now. This may
  // not be the case in the future.
  const enquiryID: string | null =
    URIParts && URIParts[5] === "monitoring" && URIParts[6] === "enquiries"
      ? URIParts[7]
      : null;

  const flagged = enquiry.flagged;
  const status = enquiry.state;

  return (
    <React.Fragment key={enquiry.id}>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            {drawerOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell component="th" scope="row">
          <Typography fontWeight={600}>
            {tzAwareHumanizeTimeString(
              enquiry.finished_at || enquiry.started_at
            )}
          </Typography>
        </TableCell>

        <TableCell component="th" scope="row">
          <Typography>{humanizeString(status)}</Typography>
        </TableCell>

        <TableCell component="th" scope="row" align="center">
          {status === "finished" &&
            (flagged ? (
              <ErrorIcon fontSize="medium" color="error" />
            ) : (
              <VerifiedIcon fontSize="medium" color="success" />
            ))}
        </TableCell>

        <TableCell component="th" scope="row">
          <Box display="flex" justifyContent="center" alignItems="center">
            {status === "finished" && (
              <IconButton
                size="medium"
                color="primary"
                disabled={downloadDisabled}
                onClick={async () => {
                  setDownloadDisabled(true);

                  try {
                    const enquiryItem = await api.getEnquiry(enquiry.id);
                    const blob = new Blob(
                      [JSON.stringify(enquiryItem.remarks, null, 2)],
                      { type: "text/plain;charset=utf-8" }
                    );
                    saveAs(
                      blob,
                      `${enquiry.domain}-${enquiryItem.id}-${Date.now()}.json`
                    );
                  } catch {
                    alert(
                      "Unable to download the API response.  Contact your account manager if this issue persists."
                    );
                  }
                  setDownloadDisabled(false);
                }}
              >
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={drawerOpen} timeout="auto" unmountOnExit>
            {enquiryID && <EnquiryResultContent enquiryID={enquiryID} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function EnquiryResultContent({ enquiryID }: { enquiryID: string }) {
  const api = useTrueBizApi();
  // We need to figure out what kind of enquiry this is based on its monitor type
  // and then render the appropriate component
  const [enquiry, setEnquiry] = useState<APIEnquiry | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadEnquiry = async () => {
      try {
        setEnquiry(await api.getEnquiry(enquiryID));
      } finally {
        setLoading(false);
      }
    };

    loadEnquiry();
  }, [api, enquiryID]);

  if (enquiry && enquiry.monitor_type === "website_status_monitor") {
    return <WebsiteStatusResult enquiry={enquiry} />;
  }
  if (enquiry && enquiry.monitor_type === "web_presence_monitor") {
    return <WebPresenceResult enquiry={enquiry} />;
  }

  return (
    <React.Fragment key="status_stuff">
      {loading && <CircularProgress size={28} sx={{ marginLeft: 1 }} />}
      {!loading && <div>No data to display.</div>}
    </React.Fragment>
  );
}
